import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAudioPlayer } from "../hooks/useAudioPlayer";

/* ---------- Helpers ---------- */

const STORAGE_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  "https://example.com/iseeabunny";

const buildUrls = (d) => {
  if (!d) return null;

  const epoch = Date.now();
  return {
    ...d,
    word: d.words?.word_text ?? "Unknown",
    image_url: `${STORAGE_BASE_URL}/gen-images/${d.dialog_id}.jpg?v=${epoch}`,
    teacher_question_url: {
      male: `${STORAGE_BASE_URL}/gen-audio/${d.dialog_id}-teacher_question-male.mp3?v=${epoch}`,
      female: `${STORAGE_BASE_URL}/gen-audio/${d.dialog_id}-teacher_question-female.mp3?v=${epoch}`,
    },
    student_answer_url: `${STORAGE_BASE_URL}/gen-audio/${d.dialog_id}-student_answer.mp3?v=${epoch}`,
    teacher_conclusion_url: {
      male: `${STORAGE_BASE_URL}/gen-audio/${d.dialog_id}-teacher_conclusion-male.mp3?v=${epoch}`,
      female: `${STORAGE_BASE_URL}/gen-audio/${d.dialog_id}-teacher_conclusion-female.mp3?v=${epoch}`,
    },
  };
};

/**
 * Fetches flagged dialogs that are already marked as reviewed (for warning)
 * Resolves with `{ dialog, flags, totalCount }` or `null`.
 */
const nextReviewedFlaggedDialog = async () => {
  // First get all flagged dialogs for reviewed dialogs
  const { data: allFlags, error: flagsError } = await supabase
    .from("flagged_dialogs")
    .select(
      `reason, profiles:profile_id(name), dialogs:dialog_id(*, words:word_id(word_text))`
    )
    .eq("dialogs.reviewed", true)
    .order("created_at", { ascending: true });

  if (flagsError) throw flagsError;
  if (!allFlags?.length) return null;

  // Group flags by dialog_id and get the earliest dialog
  const dialogGroups = allFlags.reduce((acc, flag) => {
    const dialogId = flag.dialogs?.dialog_id;
    if (!dialogId) return acc;

    if (!acc[dialogId]) {
      acc[dialogId] = {
        dialog: flag.dialogs,
        flags: [],
        earliestCreatedAt: flag.created_at || new Date().toISOString(),
      };
    }

    acc[dialogId].flags.push({
      reason: flag.reason,
      flaggedBy: flag.profiles?.name ?? "Unknown",
    });

    return acc;
  }, {});

  // Get the dialog with the earliest flag
  const earliestDialog = Object.values(dialogGroups).sort(
    (a, b) => new Date(a.earliestCreatedAt) - new Date(b.earliestCreatedAt)
  )[0];

  if (!earliestDialog) return null;

  const dialog = buildUrls(earliestDialog.dialog);
  if (!dialog) return null;

  // Get total count
  const { count, error: countError } = await supabase
    .from("flagged_dialogs")
    .select("*", { count: "exact", head: true });

  if (countError) throw countError;

  return {
    dialog,
    flags: earliestDialog.flags,
    totalCount: count || 0,
    isAlreadyReviewed: true,
  };
};

/**
 * Fetches the earliest flagged dialog that hasn't been reviewed yet.
 * Resolves with `{ dialog, flags, totalCount }` or `null`.
 */
const nextFlaggedDialog = async () => {
  // First get all flagged dialogs for unreviewed dialogs
  const { data: allFlags, error: flagsError } = await supabase
    .from("flagged_dialogs")
    .select(
      `reason, created_at, profiles:profile_id(name), dialogs:dialog_id(*, words:word_id(word_text))`
    )
    .eq("dialogs.reviewed", false)
    .order("created_at", { ascending: true });

  if (flagsError) throw flagsError;
  if (!allFlags?.length) return null;

  // Group flags by dialog_id and get the earliest dialog
  const dialogGroups = allFlags.reduce((acc, flag) => {
    const dialogId = flag.dialogs?.dialog_id;
    if (!dialogId) return acc;

    if (!acc[dialogId]) {
      acc[dialogId] = {
        dialog: flag.dialogs,
        flags: [],
        earliestCreatedAt: flag.created_at || new Date().toISOString(),
      };
    }

    acc[dialogId].flags.push({
      reason: flag.reason,
      flaggedBy: flag.profiles?.name ?? "Unknown",
    });

    return acc;
  }, {});

  // Get the dialog with the earliest flag
  const earliestDialog = Object.values(dialogGroups).sort(
    (a, b) => new Date(a.earliestCreatedAt) - new Date(b.earliestCreatedAt)
  )[0];

  if (!earliestDialog) return null;

  const dialog = buildUrls(earliestDialog.dialog);
  if (!dialog) return null;

  // Get total count
  const { count, error: countError } = await supabase
    .from("flagged_dialogs")
    .select("*", { count: "exact", head: true });

  if (countError) throw countError;

  return {
    dialog,
    flags: earliestDialog.flags,
    totalCount: count || 0,
    isAlreadyReviewed: false,
  };
};

/**
 * Returns the next dialog to review, preferring the one with the most incorrect attempts.
 * Resolves with `{dialog, incorrect_attempts_count}` or `null` when none left.
 */
const nextDialog = async () => {
  /* 1️⃣ get incorrect-attempt counts for every unreviewed dialog */
  const { data: attempts, error: attemptsErr } = await supabase
    .from("attempts")
    .select("dialog_id, dialogs!inner(reviewed)")
    .eq("is_correct", false)
    .eq("dialogs.reviewed", false);

  if (attemptsErr) throw attemptsErr;
  if (!attempts?.length) return randomDialog();

  const counts = attempts.reduce((m, { dialog_id }) => {
    m[dialog_id] = (m[dialog_id] ?? 0) + 1;
    return m;
  }, {});

  const [mostIncorrectId] = Object.entries(counts).sort(
    ([, a], [, b]) => b - a
  )[0];

  const { data, error } = await supabase
    .from("dialogs")
    .select("*, words:word_id(word_text)")
    .eq("dialog_id", mostIncorrectId)
    .single();

  if (error) throw error;
  return {
    dialog: buildUrls(data),
    incorrect_attempts_count: counts[mostIncorrectId],
  };
};

const randomDialog = async () => {
  const { count } = await supabase
    .from("dialogs")
    .select("*", { count: "exact", head: true })
    .eq("reviewed", false);

  if (!count) return null;

  const offset = Math.floor(Math.random() * count);
  const { data, error } = await supabase
    .from("dialogs")
    .select("*, words:word_id(word_text)")
    .eq("reviewed", false)
    .order("dialog_id")
    .range(offset, offset)
    .single();

  if (error) throw error;
  return { dialog: buildUrls(data) };
};

/**
 * Fetches a specific dialog by ID
 */
const fetchDialogById = async (dialogId) => {
  const { data, error } = await supabase
    .from("dialogs")
    .select("*, words:word_id(word_text)")
    .eq("dialog_id", dialogId)
    .single();

  if (error) throw error;
  return { dialog: buildUrls(data) };
};

/* ---------- UI pieces ---------- */

const ImageModal = ({ url, isOpen, onClose }) =>
  isOpen ? (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="max-w-[90vw] max-h-[90vh] relative">
        <img
          src={url}
          alt="Enlarged dialog scene"
          className="max-w-full max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
          onError={(e) =>
            (e.target.src = "https://placehold.co/600x400?text=Image+Not+Found")
          }
        />
        <button
          className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  ) : null;

/* ---------- Main component ---------- */

export default function ReviewDialogs() {
  const navigate = useNavigate();
  const { playAudioSequence } = useAudioPlayer();

  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [imageKey, setImageKey] = useState(0); // Used to force image reload
  const [generating, setGenerating] = useState(null); // 'image' | 'audio' | 'sentence' | null

  const [selectionMode, setSelectionMode] = useState("auto"); // 'auto' | 'id' | 'word'
  const [manualDialogId, setManualDialogId] = useState("");
  const [manualWord, setManualWord] = useState("");
  const [wordDialogs, setWordDialogs] = useState([]);
  const [wordDialogIndex, setWordDialogIndex] = useState(0);

  const [flaggedInfo, setFlaggedInfo] = useState(null); // { flags: [{reason, flaggedBy}], isAlreadyReviewed }
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [showReviewedWarning, setShowReviewedWarning] = useState(false);

  const play = useCallback(
    (url) => url && playAudioSequence([url]),
    [playAudioSequence]
  );

  const fetchDialog = useCallback(async (specificDialogId = null) => {
    setLoading(true);
    setError(null);
    setShowReviewedWarning(false);
    try {
      let res;
      if (specificDialogId) {
        setFlaggedInfo(null);
        setFlaggedCount(0);
        res = await fetchDialogById(specificDialogId);
      } else {
        // First, check for flagged dialogs that are already reviewed (for warning)
        const reviewedFlagged = await nextReviewedFlaggedDialog();
        if (reviewedFlagged) {
          setFlaggedInfo({
            flags: reviewedFlagged.flags,
            isAlreadyReviewed: true,
          });
          setShowReviewedWarning(true);
          setFlaggedCount(reviewedFlagged.totalCount);
          res = { dialog: reviewedFlagged.dialog };
        } else {
          // If no reviewed flagged dialogs, check for unreviewed flagged dialogs
          const flagged = await nextFlaggedDialog();
          if (flagged) {
            setFlaggedInfo({
              flags: flagged.flags,
              isAlreadyReviewed: false,
            });
            setFlaggedCount(flagged.totalCount);
            res = { dialog: flagged.dialog };
          } else {
            setFlaggedInfo(null);
            setFlaggedCount(0);
            res = await nextDialog();
          }
        }
      }

      if (!res) setDialog(null);
      else
        setDialog({
          ...res.dialog,
          incorrect_attempts_count: res.incorrect_attempts_count,
        });
    } catch (e) {
      setError(e.message ?? "Failed to fetch dialog");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectionMode === "auto") {
      fetchDialog();
    }
  }, [fetchDialog, selectionMode]);

  const handleDialogIdSubmit = async () => {
    if (!manualDialogId.trim()) {
      setError("Please enter a dialog ID");
      return;
    }
    await fetchDialog(manualDialogId.trim());
  };

  const handleWordSubmit = async () => {
    if (!manualWord.trim()) {
      setError("Please enter a word");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: wordData } = await supabase
        .from("words")
        .select("word_id")
        .eq("word_text", manualWord.trim())
        .single();

      if (!wordData) throw new Error("Word not found");

      const { data: dialogsData, error } = await supabase
        .from("dialogs")
        .select("*, words:word_id(word_text)")
        .eq("word_id", wordData.word_id)
        .order("created_at");

      if (error) throw error;

      if (!dialogsData || dialogsData.length === 0)
        throw new Error("No dialogs found for that word");

      const processed = dialogsData.map((d) => buildUrls(d));
      setWordDialogs(processed);
      setWordDialogIndex(0);
      setDialog(processed[0]);
    } catch (e) {
      setDialog(null);
      setWordDialogs([]);
      setError(e.message || "Failed to fetch dialogs for that word");
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = (mode) => {
    setSelectionMode(mode);
    setError(null);
    setDialog(null);
    setWordDialogs([]);
    setWordDialogIndex(0);
    if (mode === "auto") {
      setManualDialogId("");
      setManualWord("");
      fetchDialog(); // Fetch next automatic dialog
    }
  };

  const markReviewed = async () => {
    if (!dialog) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from("dialogs")
        .update({ reviewed: true })
        .eq("dialog_id", dialog.dialog_id);

      if (error) throw error;

      // Remove the flagged dialog row from the database if it exists
      if (flaggedInfo) {
        const { error: deleteError } = await supabase
          .from("flagged_dialogs")
          .delete()
          .eq("dialog_id", dialog.dialog_id);

        if (deleteError) {
          console.error("Error deleting flagged dialog:", deleteError);
          // Don't throw here - the main operation (marking as reviewed) succeeded
        }
      }

      if (selectionMode === "auto") {
        await fetchDialog();
      } else if (selectionMode === "id") {
        setDialog(null);
        setManualDialogId("");
      } else {
        // word mode
        const remaining = wordDialogs.filter(
          (d) => d.dialog_id !== dialog.dialog_id,
        );
        if (remaining.length) {
          setWordDialogs(remaining);
          const nextIndex = wordDialogIndex % remaining.length;
          setWordDialogIndex(nextIndex);
          setDialog(remaining[nextIndex]);
        } else {
          setDialog(null);
          setWordDialogs([]);
        }
      }
    } catch (e) {
      setError(e.message ?? "Failed to update dialog");
    } finally {
      setUpdating(false);
    }
  };

  const regenerateImage = async () => {
    if (!dialog) return;
    setGenerating("image");
    setError(null);
    try {
      const { error } = await supabase.functions.invoke("generate_image", {
        body: { sentence: dialog.sentence, dialog_id: dialog.dialog_id },
      });
      if (error) throw error;
      setImageKey((prev) => prev + 1);
    } catch (e) {
      setError(e.message || "Failed to regenerate image");
    } finally {
      setGenerating(null);
    }
  };

  const regenerateAudios = async () => {
    if (!dialog) return;
    setGenerating("audio");
    setError(null);
    try {
      const { error: funcError } = await supabase.functions.invoke(
        "generate_audio_for_dialog",
        {
          body: { dialog_id: dialog.dialog_id },
        }
      );
      if (funcError) throw funcError;
      // Refresh dialog URLs to fetch the newly generated audio without a full page reload
      setDialog((prev) => buildUrls(prev));
    } catch (e) {
      setError(e.message || "Failed to regenerate audios");
    } finally {
      setGenerating(null);
    }
  };

  const regenerateSentence = async () => {
    if (!dialog) return;
    setGenerating("sentence");
    setError(null);
    try {
      const { error } = await supabase.functions.invoke("generate_dialog", {
        body: { dialog_id: dialog.dialog_id },
      });
      if (error) throw error;
      await fetchDialog();
    } catch (e) {
      setError(e.message || "Failed to regenerate sentence");
    } finally {
      setGenerating(null);
    }
  };

  /* -------- render -------- */

  return (
    <div className="flex flex-col h-screen w-full bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-6 shadow-md">
        <div className="container mx-auto px-4 flex items-center">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center text-white mr-6"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-3xl font-bold flex-1 text-center">
            Review Dialogs
          </h1>
          <div className="w-[76px]"></div> {/* Spacer to balance the layout */}
        </div>
      </header>

      {flaggedCount > 0 && (
        <div className="bg-amber-50 text-amber-800 text-center py-2 text-sm">
          {flaggedCount} flagged dialog{flaggedCount > 1 ? "s" : ""} remaining
        </div>
      )}

      {showReviewedWarning && (
        <div className="bg-red-50 text-red-800 text-center py-2 text-sm border-b border-red-200">
          ⚠️ Warning: This dialog has already been marked as reviewed but still
          has {flaggedInfo?.flags?.length || 0} unresolved flag
          {(flaggedInfo?.flags?.length || 0) > 1 ? "s" : ""}. Click "Mark as
          Reviewed" to remove{" "}
          {(flaggedInfo?.flags?.length || 0) > 1 ? "all flags" : "the flag"}{" "}
          from the flagged list.
        </div>
      )}

      {/* Dialog Selection Controls */}
      <div className="bg-gray-50 border-b border-gray-200 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Selection Mode:
              </span>
              <button
                onClick={() => handleModeSwitch("auto")}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  selectionMode === "auto"
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Automatic
              </button>
              <button
                onClick={() => handleModeSwitch("id")}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  selectionMode === "id"
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Dialog ID
              </button>
              <button
                onClick={() => handleModeSwitch("word")}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  selectionMode === "word"
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Word
              </button>
            </div>

            {selectionMode === "id" && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter dialog ID (UUID)"
                  value={manualDialogId}
                  onChange={(e) => setManualDialogId(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handleDialogIdSubmit()
                  }
                  className="input input-bordered input-sm w-72"
                />
                <button
                  onClick={handleDialogIdSubmit}
                  disabled={loading}
                  className="btn btn-primary btn-sm"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    "Load"
                  )}
                </button>
              </div>
            )}

            {selectionMode === "word" && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter word"
                  value={manualWord}
                  onChange={(e) => setManualWord(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleWordSubmit()}
                  className="input input-bordered input-sm w-72"
                />
                <button
                  onClick={handleWordSubmit}
                  disabled={loading}
                  className="btn btn-primary btn-sm"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    "Load"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="flex-1 overflow-y-auto pb-24">
        <div className="container mx-auto px-4 py-6">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}

          {error && (
            <div className="bg-red-100 text-red-700 p-4 mb-6">
              <p>{error}</p>
              <button
                onClick={() => {
                  if (selectionMode === "auto") fetchDialog();
                  else if (selectionMode === "id") handleDialogIdSubmit();
                  else handleWordSubmit();
                }}
                className="btn btn-sm btn-outline mt-2"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && !dialog && (
            <div className="text-center py-8">
              {selectionMode === "id" ? (
                <>
                  <h2 className="text-2xl font-bold mb-2">
                    Enter Dialog ID to Review
                  </h2>
                  <p className="text-gray-600">
                    Use the input field above to enter a specific dialog ID to
                    review.
                  </p>
                </>
              ) : selectionMode === "word" ? (
                <>
                  <h2 className="text-2xl font-bold mb-2">Enter Word to Review</h2>
                  <p className="text-gray-600">
                    Use the input field above to load dialogs for a word.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-2">
                    No Dialogs Left to Review
                  </h2>
                  <p className="text-gray-600">
                    All dialogs have been reviewed. Great job!
                  </p>
                </>
              )}
            </div>
          )}

          {!loading && dialog && (
            <>
              <ImageModal
                url={`${dialog.image_url}?key=${imageKey}`}
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
              />

              <div className="flex flex-col md:flex-row gap-8 max-w-6xl mx-auto">
                {/* left column */}
                <aside className="md:w-1/3 space-y-4">
                  <div className="relative">
                    <img
                      key={imageKey}
                      src={`${dialog.image_url}?key=${imageKey}`}
                      alt="Dialog scene"
                      className="w-full object-contain max-h-[350px] cursor-pointer hover:opacity-90"
                      onClick={() => setModalOpen(true)}
                      onError={(e) =>
                        (e.target.src =
                          "https://placehold.co/600x400?text=Image+Not+Found")
                      }
                    />
                    <div className="mt-2 w-full flex gap-2 flex-col">
                      <div className="flex gap-2">
                        <LoadingButton
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center disabled:opacity-50"
                          onClick={regenerateImage}
                          loading={generating === "image"}
                        >
                          Re-generate Image
                        </LoadingButton>
                        <LoadingButton
                          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center disabled:opacity-50"
                          onClick={regenerateAudios}
                          loading={generating === "audio"}
                        >
                          Re-generate Audios
                        </LoadingButton>
                      </div>
                  <LoadingButton
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md flex items-center justify-center disabled:opacity-50 mt-2"
                    onClick={regenerateSentence}
                    loading={generating === "sentence"}
                  >
                    Re-generate Sentence
                  </LoadingButton>
                  {selectionMode === "word" && wordDialogs.length > 1 && (
                    <div className="flex justify-between items-center mt-4">
                      <button
                        className="btn btn-sm"
                        onClick={() =>
                          setWordDialogIndex((i) => {
                            const ni = (i - 1 + wordDialogs.length) % wordDialogs.length;
                            setDialog(wordDialogs[ni]);
                            return ni;
                          })
                        }
                      >
                        Prev
                      </button>
                      <span className="text-sm">
                        {wordDialogIndex + 1} of {wordDialogs.length}
                      </span>
                      <button
                        className="btn btn-sm"
                        onClick={() =>
                          setWordDialogIndex((i) => {
                            const ni = (i + 1) % wordDialogs.length;
                            setDialog(wordDialogs[ni]);
                            return ni;
                          })
                        }
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

                  <InfoBlock label="Word" value={dialog.word} />
                  <InfoBlock label="Dialog ID" value={dialog.dialog_id} mono />
                  <InfoBlock
                    label="Is Correct"
                    value={dialog.is_correct ? "✅" : "❌"}
                  />
                  {dialog.incorrect_attempts_count && (
                    <InfoBlock
                      label="Incorrect Attempts"
                      value={dialog.incorrect_attempts_count}
                      className="border-l-4 border-amber-500"
                    />
                  )}
                  {flaggedInfo && (
                    <>
                      <div
                        className={`bg-gray-50 p-4 border-l-4 ${
                          flaggedInfo.isAlreadyReviewed
                            ? "border-orange-500 bg-orange-50"
                            : "border-red-500"
                        }`}
                      >
                        <h3 className="font-medium text-sm text-gray-500 mb-2">
                          Flagged Issues ({flaggedInfo.flags.length})
                        </h3>
                        <div className="space-y-2">
                          {flaggedInfo.flags.map((flag, index) => (
                            <div key={index} className="text-sm">
                              <div className="font-medium text-gray-800">
                                {flag.reason}
                              </div>
                              <div className="text-gray-600">
                                — {flag.flaggedBy}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {flaggedInfo.isAlreadyReviewed && (
                        <InfoBlock
                          label="Status"
                          value="⚠️ Already Reviewed"
                          className="border-l-4 border-orange-500 bg-orange-50"
                        />
                      )}
                    </>
                  )}
                </aside>

                {/* right column */}
                <section className="md:w-2/3 space-y-6">
                  <TextBlock label="Sentence" text={dialog.sentence} />

                  <AudioBlock
                    label="Teacher Question"
                    text={dialog.teacher_question}
                    buttons={[
                      { title: "Male", url: dialog.teacher_question_url.male },
                      {
                        title: "Female",
                        url: dialog.teacher_question_url.female,
                      },
                    ]}
                    onPlay={play}
                  />

                  <AudioBlock
                    label="Student Answer"
                    text={dialog.student_answer}
                    buttons={[
                      { title: "Play", url: dialog.student_answer_url },
                    ]}
                    onPlay={play}
                  />

                  <AudioBlock
                    label="Teacher Conclusion"
                    text={dialog.teacher_conclusion}
                    buttons={[
                      {
                        title: "Male",
                        url: dialog.teacher_conclusion_url.male,
                      },
                      {
                        title: "Female",
                        url: dialog.teacher_conclusion_url.female,
                      },
                    ]}
                    onPlay={play}
                  />

                  <div className="flex justify-end pt-8">
                    <LoadingButton
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-md"
                      onClick={markReviewed}
                      loading={updating}
                    >
                      Mark as Reviewed
                    </LoadingButton>
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

/* ---------- Small UI helpers ---------- */

const InfoBlock = ({ label, value, mono, className = "" }) => (
  <div className={`bg-gray-50 p-4 ${className}`}>
    <h3 className="font-medium text-sm text-gray-500 mb-1">{label}</h3>
    <p className={`${mono ? "font-mono text-sm" : "text-lg"} text-gray-800`}>
      {value}
    </p>
  </div>
);

const TextBlock = ({ label, text }) => (
  <div className="bg-gray-50 p-6">
    <h3 className="font-medium text-base mb-2 text-gray-500">{label}</h3>
    <p className="text-lg text-gray-800">{text}</p>
  </div>
);

const AudioBlock = ({ label, text, buttons, onPlay }) => (
  <div className="bg-gray-50 p-6">
    <div className="flex flex-wrap justify-between items-center mb-3">
      <h3 className="font-medium text-base text-gray-500">{label}</h3>
      <div className="flex gap-2">
        {buttons.map(({ title, url }) => (
          <button
            key={title}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-full text-sm"
            onClick={() => onPlay(url)}
          >
            {title}
          </button>
        ))}
      </div>
    </div>
    <p className="text-lg text-gray-800">{text}</p>
  </div>
);

// LoadingButton component for DRY loading button logic
const LoadingButton = ({ loading, children, ...props }) => (
  <button {...props} disabled={loading || props.disabled}>
    {loading ? (
      <>
        <span className="loading loading-spinner loading-sm mr-2"></span>
        {typeof children === "string"
          ? `Generating${
              children.includes("Image")
                ? "..."
                : children.includes("Audio")
                ? " Audios..."
                : children.includes("Sentence")
                ? " Sentence..."
                : "..."
            }`
          : children}
      </>
    ) : (
      children
    )}
  </button>
);
